import { dequal } from 'dequal';

/**
 * Useful to deduplicate async calls.
 *
 * @example
 * ```ts
 * const fetchProjectsPromise = usePromise()
 *
 * async function fetchProjects() {
 *   if (!fetchProjectPromise.pending) {
 *     fetchProjectPromise.create()
 *     await doStuff()
 *     fetchProjectPromise.resolve()
 *   }
 *   else {
 *     await fetchProjectPromise.wait()
 *   }
 * }
 * ```
 */
export function usePromiseDedupe() {
	let readyResolve: () => void;

	function createPromise() {
		return new Promise<void>((resolve) => {
			readyResolve = resolve;
		});
	}

	let ready: Promise<void> = createPromise();
	let pending = false;

	return {
		create() {
			readyResolve();
			ready = createPromise();
			pending = true;
		},
		resolve() {
			readyResolve();
			pending = false;
		},
		wait() {
			return ready;
		},
		get pending() {
			return pending;
		},
	};
}

/**
 * Dedupe an async operation based on its arguments.
 * @param op The async operation to dedupe.
 * @returns The deduped async operation.
 */
export function dedupePromise<TFunction extends ((...args: any[]) => any)>(op: TFunction): TFunction & { $reset: () => void; $force: TFunction } {
	const promise = usePromiseDedupe();
	let currentArgs: Parameters<TFunction> | null = null;
	let result: ReturnType<TFunction> | null = null;

	async function callOp(...args: Parameters<TFunction>) {
		if (currentArgs === null || !dequal(currentArgs, args)) {
			currentArgs = args;
			promise.create();
			result = await op(...args);
			promise.resolve();
		}
		else {
			await promise.wait();
		}

		return result;
	}

	function reset() {
		currentArgs = null;
		result = null;
	}

	async function force(...args: Parameters<TFunction>) {
		reset();
		return callOp(...args);
	}

	return Object.assign(callOp, {
		$reset: reset,
		$force: force,
	}) as any;
}
